import { jsx } from "panel";
import uniq from "lodash/uniq";
import {
  FiltersOperator,
  RowLabelType
} from "common/query-builder/query-entry-section/types";
import { QueryBlockEl } from "common/query-builder/query-block";
import { ButtonSize } from "common/components/button/constants";
import { generateStepRanges } from "common/widgets/step-range-selector/util";
import { SelectTheme } from "common/components/select/constants";
import {
  CONVERSION_WINDOW_SIZE_LIMITS,
  CONVERSION_WINDOW_UNITS,
  MAX_HOLDING_PROPERTY_CONSTANT,
  ReportName,
  ResourceType
} from "common/report/constants";
import { ConversionQueryOptionsEl } from "common/query-builder/conversion-query-options";
import { renameEvent } from "common/query-builder/cohorts-query-builder/util";
import { QueryEntrySectionEl } from "common/query-builder/query-entry-section/row-types/base/query-entry-section-el";
import { BaseEntryRowEl } from "common/query-builder/query-entry-section/row-types/base/template";
import { MetricType } from "common/query-builder/query-block/constants";
export function FunnelsMetricEntryRowEl({ section, entry, index }) {
  const behaviorRenderer = () => /* @__PURE__ */ jsx("div", {
    sel: ".mp-metric-entry-behavior"
  }, /* @__PURE__ */ jsx(QueryEntrySectionEl, {
    key: "funnelEntries",
    attrs: {
      ...section.helpers.getNestedSectionAttrs(),
      elref: `entrySection`,
      autofocus: true,
      "autoactivate-placeholders": section.getEntries().length > 1,
      "add-entry-label": `Select Step`,
      "add-entry-button-label": `Add Step`,
      "add-entry-button-tooltip-text": `Analyze users who performed each event.
[Read Intro to Funnels](https://docs.mixpanel.com/docs/analysis/reports/funnels)`,
      "min-entries": 2,
      "empty-placeholder": {
        entryIndexLabelToggle: entry.behavior.entryIndexLabelToggle,
        event: {
          custom: false,
          id: null,
          label: `Select Step`,
          renamed: null,
          value: null
        },
        filters: [],
        filtersOperator: FiltersOperator.And,
        type: ResourceType.Event
      },
      "row-draggable": true,
      "row-types": [ResourceType.Event],
      "row-label-type": RowLabelType.Numeric,
      "report-name": ReportName.Funnels,
      entries: entry.behavior.behaviors.map((behavior) => ({
        entryIndexLabelToggle: entry.behavior.entryIndexLabelToggle,
        event: {
          custom: behavior.type === MetricType.CustomEvent,
          id: behavior.id,
          label: renameEvent(behavior.name) || `Select Step`,
          renamed: behavior.renamed,
          value: behavior.name
        },
        filters: behavior.filters,
        filtersOperator: behavior.filtersOperator,
        type: ResourceType.Event
      }))
    },
    on: {
      update: this.handleEntryUpdate
    }
  }), /* @__PURE__ */ jsx(ConversionQueryOptionsEl, {
    attrs: {
      "event-names": uniq(entry.behavior.behaviors.map((behavior) => behavior.name)),
      "include-custom-properties": `all`,
      "project-id": section.attr(`project-id`),
      "hide-count-selector": true,
      "workspace-id": section.attr(`workspace-id`),
      selected: {
        conversionWindow: {
          unit: entry.behavior.conversionWindowUnit,
          value: entry.behavior.conversionWindowDuration
        },
        countType: entry.measurement.aggregationOperator,
        holdingPropertyConstant: entry.behavior.aggregateBy,
        funnelOrder: entry.behavior.funnelOrder
      },
      "show-sessions": true,
      "builder-params": {
        entries: [],
        exclusionSteps: [],
        dataGroupsEnabled: false,
        dataGroupId: null,
        projectHasPeople: true,
        projectHasCohorts: true,
        projectId: section.attr(`project-id`),
        workspaceId: section.attr(`workspace-id`),
        trackName: ``,
        projectAsOfDate: ``,
        entryRowLabelType: RowLabelType.None
      },
      "max-holding-property-constant": MAX_HOLDING_PROPERTY_CONSTANT,
      "conversion-window-units": CONVERSION_WINDOW_UNITS,
      "conversion-window-size-limits": CONVERSION_WINDOW_SIZE_LIMITS,
      "hide-any-order": false,
      "hide-conversion-window": false
    },
    on: {
      changeQueryOption: this.handleFunnelsConversionCriterialChanged
    }
  }));
  const measurementRenderer = () => /* @__PURE__ */ jsx("div", {
    sel: ".mp-metric-entry-measurement"
  }, /* @__PURE__ */ jsx(QueryBlockEl, {
    sel: ".count-menu",
    attrs: {
      "advanced-menu-sections": [
        {
          icon: `info-xs`,
          tooltip: { text: `Select the completed step to measure from` },
          label: `Completed Steps`,
          items: generateStepRanges({
            entry: {
              isEntireRange: entry.measurement.stepIndex === null,
              range: entry.measurement.stepIndex === null ? {} : {
                from: entry.measurement.stepIndex || 1,
                to: (entry.measurement.stepIndex || 1) + 1
              }
            },
            steps: entry.behavior.behaviors.length,
            rowLabelType: RowLabelType.Numeric,
            disableAllSteps: false,
            singleStep: true
          })
        }
      ],
      elref: `countBlock`,
      type: `count`,
      "metric-type": MetricType.Funnel,
      "data-group-id": section.attr(`data-group-id`),
      "project-id": section.attr(`project-id`),
      "selector-type": `button`,
      "event-names": entry.behavior.behaviors.map((behavior) => behavior.name),
      selected: {
        countType: entry.measurement.aggregationOperator,
        aggregationOperator: entry.measurement.aggregationOperator,
        advancedMenuValue: {
          isEntireRange: entry.measurement.stepIndex === null,
          range: entry.measurement.stepIndex === null ? {} : {
            from: entry.measurement.stepIndex,
            to: entry.measurement.stepIndex + 1
          }
        }
      },
      "highlight-selections": true,
      theme: SelectTheme.QueryBuilder,
      "button-size": ButtonSize.Small,
      "track-name": `funnels`,
      "user-id": section.attr(`user-id`),
      "workspace-id": section.attr(`workspace-id`)
    },
    on: {
      select: this.handleFunnelsCountBySelected
    }
  }));
  return /* @__PURE__ */ jsx(BaseEntryRowEl, {
    el: section,
    entry,
    index,
    showLabels: false,
    hideActions: true,
    rowRenderer: {
      selectorRenderer: behaviorRenderer,
      bodyRowRenderer: measurementRenderer
    },
    isPlaceholder: this.isPlaceholder()
  });
}
